import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatMoney } from "@/lib/entryFieldConfig";
import { Anchor, Calendar, Download, FileDown, Fuel, Gauge, Image as ImageIcon, MapPin, Ship } from "lucide-react";

/**
 * SeasonSummary — Pro-only recap card for a captain's cruising season.
 * Shows totals for distance, engine hours, fuel volume, and $$$ over a
 * chosen date range (calendar year / cruising season / custom). Includes
 * PDF export + share-as-image via a pure <canvas> render (no new deps).
 */

const cruisingYearFor = (now = new Date()) => now.getFullYear();

const RANGE_PRESETS = [
    { id: "calendar", label: "Calendar Year" },
    { id: "cruising", label: "Cruising Season (Apr–Oct)" },
    { id: "custom", label: "Custom Range" },
];

function pad(n) { return String(n).padStart(2, "0"); }
function isoToday() {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function calcRangeFor(preset, year, custom) {
    if (preset === "custom") return { start: custom.start, end: custom.end };
    if (preset === "cruising") return { start: `${year}-04-01`, end: `${year}-10-31` };
    // calendar
    const cy = cruisingYearFor();
    const end = year === cy ? isoToday() : `${year}-12-31`;
    return { start: `${year}-01-01`, end };
}

function fmtNum(n, digits = 1) {
    if (n == null || Number.isNaN(n)) return "—";
    return Number(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export default function SeasonSummary({ vessel }) {
    const [preset, setPreset] = useState("calendar");
    const currentYear = cruisingYearFor();
    const [year, setYear] = useState(currentYear);
    const [custom, setCustom] = useState({ start: `${currentYear}-04-01`, end: isoToday() });
    const [totals, setTotals] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [pdfBusy, setPdfBusy] = useState(false);
    const [shareBusy, setShareBusy] = useState(false);

    const range = useMemo(() => calcRangeFor(preset, year, custom), [preset, year, custom]);

    // Any range change resets the error text so a stale "bad range" message
    // never lingers after the user toggles back to a valid preset.
    useEffect(() => { setErr(""); }, [range.start, range.end]);

    const load = useCallback(async () => {
        if (!vessel?.id) return;
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.get(`/vessels/${vessel.id}/season-summary`, {
                params: { start: range.start, end: range.end },
            });
            setTotals(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load season summary.");
            setTotals(null);
        } finally {
            setBusy(false);
        }
    }, [vessel?.id, range.start, range.end]);

    useEffect(() => { load(); }, [load]);

    // Available years — go back 5 seasons, forward 1 for planners
    const yearOptions = useMemo(() => {
        const cy = cruisingYearFor();
        const out = [];
        for (let y = cy + 1; y >= cy - 5; y--) out.push(y);
        return out;
    }, []);

    const currency = totals?.currency || (vessel?.display_currency || "USD");
    const volumeLbl = totals?.fuel_unit === "L" ? "L" : (totals?.fuel_unit === "mixed" ? "units" : "gal");
    const mpgLbl = volumeLbl === "L" ? "nm / L" : (volumeLbl === "units" ? "nm / unit" : "nm / gal");
    const gphLbl = volumeLbl === "L" ? "L / h" : (volumeLbl === "units" ? "unit / h" : "gal / h");

    const downloadPdf = async () => {
        if (!vessel?.id || pdfBusy) return;
        setPdfBusy(true);
        try {
            const resp = await api.get(`/vessels/${vessel.id}/season-summary/export-pdf`, {
                params: { start: range.start, end: range.end },
                responseType: "blob",
            });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${vessel.name.replace(/\s+/g, "_")}_season_${range.start}_to_${range.end}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "PDF export failed.");
        } finally {
            setPdfBusy(false);
        }
    };

    const shareAsImage = async () => {
        if (!totals || !vessel || shareBusy) return;
        setShareBusy(true);
        try {
            const blob = await renderSeasonCardImage({ vessel, totals, currency, volumeLbl });
            // Try native share sheet first (mobile + supported desktops), fall back to download
            const file = new File([blob], `season_${range.start}_to_${range.end}.png`, { type: "image/png" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                try {
                    await navigator.share({ files: [file], title: `${vessel.name} — Season ${range.start.slice(0, 4)}`, text: "My cruising season on Bosun's Mate" });
                    return;
                } catch (shareErr) {
                    // User canceled or share failed — fall through to download
                    if (shareErr?.name === "AbortError") return;
                }
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${vessel.name.replace(/\s+/g, "_")}_season_${range.start}_to_${range.end}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error("[season summary] share image failed", e);
            setErr("Could not generate share image.");
        } finally {
            setShareBusy(false);
        }
    };

    return (
        <div data-testid="season-summary-view">
            <header style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--bm-gold)" }}>Season Summary</div>
                <h2 className="bm-serif" style={{ fontSize: 30, margin: "6px 0 6px", fontWeight: 500, color: "var(--bm-text)" }}>
                    A season on the water.
                </h2>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 14, margin: 0 }}>
                    Distance, engine hours, fuel and total cost — rolled up for the window you pick.
                </p>
            </header>

            {/* Range selector */}
            <div className="bm-card" style={{ padding: 18, marginBottom: 20 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                    {RANGE_PRESETS.map((p) => (
                        <button
                            key={p.id}
                            data-testid={`season-preset-${p.id}`}
                            onClick={() => setPreset(p.id)}
                            style={{
                                padding: "8px 14px",
                                borderRadius: 999,
                                border: `1px solid ${preset === p.id ? "var(--bm-gold)" : "var(--bm-border-soft)"}`,
                                background: preset === p.id ? "rgba(212,181,114,0.08)" : "transparent",
                                color: preset === p.id ? "var(--bm-gold)" : "var(--bm-text-muted)",
                                fontSize: 13,
                                cursor: "pointer",
                            }}
                        >
                            {p.label}
                        </button>
                    ))}
                    {preset !== "custom" && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                            <Calendar size={14} color="var(--bm-text-muted)" />
                            <select
                                data-testid="season-year-select"
                                value={year}
                                onChange={(e) => setYear(Number(e.target.value))}
                                className="bm-input"
                                style={{ padding: "6px 10px", fontSize: 13, minWidth: 100 }}
                            >
                                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                {preset === "custom" && (
                    <div style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
                        <div>
                            <label style={{ display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)", marginBottom: 4 }}>Start</label>
                            <input
                                data-testid="season-custom-start"
                                type="date"
                                value={custom.start}
                                onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                                className="bm-input"
                                style={{ padding: "6px 10px", fontSize: 13 }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-dim)", marginBottom: 4 }}>End</label>
                            <input
                                data-testid="season-custom-end"
                                type="date"
                                value={custom.end}
                                onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                                className="bm-input"
                                style={{ padding: "6px 10px", fontSize: 13 }}
                            />
                        </div>
                    </div>
                )}
                <div style={{ marginTop: 12, fontSize: 12, color: "var(--bm-text-muted)" }} data-testid="season-range-label">
                    Showing <b style={{ color: "var(--bm-text)" }}>{range.start}</b> → <b style={{ color: "var(--bm-text)" }}>{range.end}</b>
                </div>
            </div>

            {err && (
                <div data-testid="season-error" style={{ color: "var(--bm-bad)", marginBottom: 14 }}>{err}</div>
            )}

            {/* Headline totals */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 18 }}>
                <HeroTile
                    testid="season-distance"
                    icon={<MapPin size={18} />}
                    label="Distance covered"
                    value={busy ? "…" : `${fmtNum(totals?.distance_nm, 1)} nm`}
                    subtitle={totals?.avg_speed_kn > 0 ? `avg ${fmtNum(totals.avg_speed_kn, 1)} kn` : null}
                />
                <HeroTile
                    testid="season-engine-hours"
                    icon={<Gauge size={18} />}
                    label="Engine hours underway"
                    value={busy ? "…" : `${fmtNum(totals?.engine_hours_underway, 1)} h`}
                    subtitle={totals?.cruising_log_days ? `${totals.cruising_log_days} day${totals.cruising_log_days === 1 ? "" : "s"} on the water` : null}
                />
                <HeroTile
                    testid="season-fuel-volume"
                    icon={<Fuel size={18} />}
                    label="Fuel used"
                    value={busy ? "…" : `${fmtNum(totals?.fuel_volume, 1)} ${volumeLbl}`}
                    subtitle={totals?.fuel_fill_ups ? `${totals.fuel_fill_ups} fill-up${totals.fuel_fill_ups === 1 ? "" : "s"}` : null}
                />
                <HeroTile
                    testid="season-total-cost"
                    icon={<Ship size={18} />}
                    label="Total spend"
                    value={busy ? "…" : formatMoney(totals?.total_cost || 0, currency)}
                    subtitle={totals ? `${formatMoney(totals.fuel_cost || 0, currency)} fuel · ${formatMoney(totals.other_cost || 0, currency)} other` : null}
                    accent
                />
            </div>

            {/* Averages row */}
            <div className="bm-card" style={{ padding: 22, marginBottom: 22 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--bm-text-dim)", marginBottom: 14 }}>Averages</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20 }}>
                    <AvgTile testid="season-avg-mpg" label={mpgLbl} value={fmtNum(totals?.avg_mpg, 2)} />
                    <AvgTile testid="season-avg-gph" label={gphLbl} value={fmtNum(totals?.avg_gph, 2)} />
                    <AvgTile testid="season-cost-per-nm" label={`${currency} / nm`} value={fmtNum(totals?.cost_per_nm, 2)} />
                    <AvgTile testid="season-cost-per-hour" label={`${currency} / hour`} value={fmtNum(totals?.cost_per_hour, 2)} />
                </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <button
                    data-testid="season-download-pdf-btn"
                    onClick={downloadPdf}
                    disabled={pdfBusy || busy}
                    className="bm-btn bm-btn-ghost"
                    style={{ padding: "10px 18px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                    <FileDown size={15} />
                    {pdfBusy ? "Preparing PDF…" : "Download PDF recap"}
                </button>
                <button
                    data-testid="season-share-image-btn"
                    onClick={shareAsImage}
                    disabled={shareBusy || busy || !totals}
                    className="bm-btn bm-btn-gold"
                    style={{ padding: "10px 18px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                    <ImageIcon size={15} />
                    {shareBusy ? "Rendering…" : "Share as image"}
                </button>
            </div>

            {!busy && totals && (
                totals.distance_nm === 0 &&
                totals.total_cost === 0 &&
                totals.fuel_volume === 0 &&
                totals.engine_hours_underway === 0 &&
                totals.cruising_log_days === 0
            ) && (
                <div data-testid="season-empty" style={{ marginTop: 22, padding: 18, borderRadius: 14, border: "1px dashed var(--bm-border-soft)", textAlign: "center", color: "var(--bm-text-muted)", fontSize: 14 }}>
                    <Anchor size={20} style={{ marginBottom: 6, opacity: 0.6 }} />
                    <div>No cruising, fuel or costs logged in this window yet.</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Log a few days on the water and this recap will fill itself in.</div>
                </div>
            )}
        </div>
    );
}

function HeroTile({ icon, label, value, subtitle, accent, testid }) {
    return (
        <div
            data-testid={testid}
            className="bm-card"
            style={{
                padding: "18px 20px",
                border: accent ? "1px solid var(--bm-gold-dim, rgba(212,181,114,0.35))" : undefined,
                background: accent ? "linear-gradient(135deg, rgba(212,181,114,0.08), rgba(212,181,114,0.02))" : undefined,
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: accent ? "var(--bm-gold)" : "var(--bm-text-muted)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                {icon}<span>{label}</span>
            </div>
            <div className="bm-serif" style={{ fontSize: 32, marginTop: 8, color: accent ? "var(--bm-gold)" : "var(--bm-text)", fontVariantNumeric: "tabular-nums" }}>
                {value}
            </div>
            {subtitle && (
                <div style={{ marginTop: 4, fontSize: 12, color: "var(--bm-text-muted)" }}>{subtitle}</div>
            )}
        </div>
    );
}

function AvgTile({ label, value, testid }) {
    return (
        <div data-testid={testid}>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--bm-text-dim)" }}>{label}</div>
            <div className="bm-serif" style={{ fontSize: 22, marginTop: 4, color: "var(--bm-text)", fontVariantNumeric: "tabular-nums" }}>{value}</div>
        </div>
    );
}

// ---- pure <canvas> share-as-image renderer ----
async function renderSeasonCardImage({ vessel, totals, currency, volumeLbl }) {
    const W = 1200;
    const H = 1200; // square works well for IG feed + LinkedIn
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Deep navy background
    ctx.fillStyle = "#050f1c";
    ctx.fillRect(0, 0, W, H);

    // Subtle gold-tinted radial glow, top-right
    const grad = ctx.createRadialGradient(W - 200, 200, 100, W - 200, 200, 900);
    grad.addColorStop(0, "rgba(212,181,114,0.18)");
    grad.addColorStop(1, "rgba(212,181,114,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Anchor glyph as a decorative watermark (pure text)
    ctx.fillStyle = "rgba(212,181,114,0.06)";
    ctx.font = "bold 520px 'Georgia', serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("⚓", W / 2, H / 2 + 80);

    // Eyebrow
    ctx.fillStyle = "#d4b572";
    ctx.font = "600 22px 'Helvetica', sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("BOSUN'S MATE · SEASON SUMMARY", 72, 72);

    // Vessel name (serif)
    ctx.fillStyle = "#f5efe1";
    ctx.font = "italic 500 68px 'Georgia', serif";
    const vesselName = (vessel?.name || "Unnamed").toString();
    // Wrap if extremely long
    const nameFits = ctx.measureText(vesselName).width <= W - 144;
    if (nameFits) {
        ctx.fillText(vesselName, 72, 118);
    } else {
        ctx.font = "italic 500 52px 'Georgia', serif";
        ctx.fillText(vesselName, 72, 124);
    }

    // Date range
    ctx.fillStyle = "#8a9bb3";
    ctx.font = "400 24px 'Helvetica', sans-serif";
    const range = `${totals.start}  →  ${totals.end}`;
    ctx.fillText(range, 72, 208);

    // Divider line
    ctx.strokeStyle = "rgba(212,181,114,0.4)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(72, 258);
    ctx.lineTo(W - 72, 258);
    ctx.stroke();

    // 4 hero stats in a 2×2 grid
    const stats = [
        { label: "DISTANCE", value: `${fmtNum(totals.distance_nm, 1)} nm` },
        { label: "ENGINE HOURS", value: `${fmtNum(totals.engine_hours_underway, 1)} h` },
        { label: "FUEL USED", value: `${fmtNum(totals.fuel_volume, 1)} ${volumeLbl}` },
        { label: "TOTAL SPEND", value: `${formatMoney(totals.total_cost || 0, currency)}` },
    ];
    const gridTop = 320;
    const gridLeft = 72;
    const colW = (W - 144) / 2;
    const rowH = 290;
    stats.forEach((stat, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const cx = gridLeft + col * colW;
        const cy = gridTop + row * rowH;
        ctx.fillStyle = "#8a9bb3";
        ctx.font = "600 20px 'Helvetica', sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(stat.label, cx, cy);
        ctx.fillStyle = i === 3 ? "#d4b572" : "#f5efe1"; // highlight total spend
        ctx.font = "500 76px 'Georgia', serif";
        ctx.fillText(stat.value, cx, cy + 34);
    });

    // Bottom band — averages line
    const avgY = H - 200;
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(72, avgY);
    ctx.lineTo(W - 72, avgY);
    ctx.stroke();

    const gphLbl = volumeLbl === "L" ? "L/h" : (volumeLbl === "units" ? "unit/h" : "GPH");
    const mpgLbl = volumeLbl === "L" ? "nm/L" : (volumeLbl === "units" ? "nm/unit" : "MPG");

    const avgs = [
        { label: mpgLbl, value: fmtNum(totals.avg_mpg, 2) },
        { label: gphLbl, value: fmtNum(totals.avg_gph, 2) },
        { label: `${currency}/nm`, value: fmtNum(totals.cost_per_nm, 2) },
        { label: `${currency}/hr`, value: fmtNum(totals.cost_per_hour, 2) },
    ];
    const avgColW = (W - 144) / 4;
    avgs.forEach((a, i) => {
        const cx = 72 + i * avgColW + avgColW / 2;
        ctx.fillStyle = "#8a9bb3";
        ctx.font = "600 15px 'Helvetica', sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(a.label, cx, avgY + 32);
        ctx.fillStyle = "#f5efe1";
        ctx.font = "500 34px 'Georgia', serif";
        ctx.fillText(a.value, cx, avgY + 58);
    });

    // Footer
    ctx.fillStyle = "#8a9bb3";
    ctx.font = "400 18px 'Helvetica', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText("bosunsmate.app · the captain's logbook & network", W / 2, H - 44);

    return await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}
